import { useState, useEffect, useCallback, useMemo } from 'react';
import { feriadoService } from '../../../services';
import { receivedDemandsService } from '../../../services/receivedDemands';
import type { Feriado, Demanda } from '../../../models';
import { toast } from '../../../components/Toast';

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  type: 'holiday' | 'demand';
  data: Feriado | Demanda;
}

export const useCalendarData = () => {
  const [feriados, setFeriados] = useState<Feriado[]>([]);
  const [demandas, setDemandas] = useState<Demanda[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFeriados = useCallback(async (ano?: number) => {
    try {
      const data = await feriadoService.getAll(ano ? { ano } : undefined);
      setFeriados(data);
      return data;
    } catch (err) {
      console.error('Erro ao buscar feriados:', err);
      toast.error('Não foi possível carregar os feriados.');
      throw err;
    }
  }, []);

  const fetchDemandas = useCallback(async () => {
    try {
      // Use receivedDemandsService.getDemandas() to get only demands visible to the user
      // This respects sector permissions and visibility rules
      const data = await receivedDemandsService.getDemandas();
      // Filter only demands with delivery dates
      const demandasComEntrega = data.filter(d => d.previsao_entrega);
      setDemandas(demandasComEntrega);
      return demandasComEntrega;
    } catch (err) {
      console.error('Erro ao buscar demandas:', err);
      toast.error('Não foi possível carregar as demandas.');
      throw err;
    }
  }, []);

  const loadData = useCallback(async (ano?: number) => {
    setIsLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        fetchFeriados(ano),
        fetchDemandas()
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados do calendário:', error);
      setError('Erro ao carregar dados do calendário');
    } finally {
      setIsLoading(false);
    }
  }, [fetchFeriados, fetchDemandas]);

  // Load data on mount
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    loadData(currentYear);
  }, [loadData]);

  // Convert feriados and demandas to calendar events
  const events: CalendarEvent[] = useMemo(() => [
    ...feriados.map(feriado => ({
      id: `feriado-${feriado.id}`,
      title: feriado.descricao,
      start: new Date(feriado.data + 'T00:00:00'),
      end: new Date(feriado.data + 'T23:59:59'),
      type: 'holiday' as const,
      data: feriado
    })),
    ...demandas.map(demanda => ({
      id: `demanda-${demanda.id}`,
      title: demanda.titulo,
      start: new Date(demanda.previsao_entrega + 'T00:00:00'),
      end: new Date(demanda.previsao_entrega + 'T23:59:59'),
      type: 'demand' as const,
      data: demanda
    }))
  ], [feriados, demandas]);

  // Helper to get events for a specific date
  const getEventsForDate = useCallback((date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => {
      const eventDateStr = event.start.toISOString().split('T')[0];
      return eventDateStr === dateStr;
    });
  }, [events]);

  // Helper to get holidays for a specific date
  const getHolidaysForDate = useCallback((date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return feriados.filter(feriado => {
      const feriadoDateStr = feriado.data;
      return feriadoDateStr === dateStr;
    });
  }, [feriados]);

  return {
    feriados,
    demandas,
    events,
    isLoading,
    error,
    refetch: loadData,
    fetchFeriados,
    fetchDemandas,
    getEventsForDate,
    getHolidaysForDate
  };
};

